import { UserIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useAddEvent } from "../../../../hooks/track/useAddEvent";
import { useUpdateEvent } from "../../../../hooks/track/useUpdateEvent";
import { useUserProviders } from "../../../../hooks/useUserProviders";
import { useAnalytics } from "../../../../lib/analytics";
import { useClientTranslations } from "../../../../lib/i18n/client";
import {
	TRACK_EVENT_CREATED,
	TRACK_EVENT_EDITED,
} from "../../../../lib/track/analyticsEvents";
import { parseSyntheticEventId } from "../../../../lib/track/parseSyntheticEventId";
import type {
	CustomFrequencyState,
	Weekday,
} from "../../../../lib/track/serializeRRule";
import {
	type MedicationFormState,
	validateMedicationForm,
} from "../../../../services/formValidationService";
import type { CreateEventInput, Event } from "../../../../types/event";
import {
	DatePicker,
	FormField,
	FrequencyPicker,
	type FrequencyValue,
	Select,
	type SelectOption,
	TimePicker,
} from "../fields";
import { RecurringSeriesBanner } from "../RecurringSeriesBanner";
import { EventFormShell } from "./EventFormShell";
import styles from "./MedicationForm.module.css";

type FormMode = "create" | "edit";

interface MedicationFormProps {
	mode: FormMode;
	initialValues?: Event;
	onClose: () => void;
}

// WR-06: runtime weekday-token validation for parseRRuleToFrequency hydration.
const VALID_WEEKDAYS: ReadonlySet<Weekday> = new Set([
	"MO",
	"TU",
	"WE",
	"TH",
	"FR",
	"SA",
	"SU",
]);

// Curated medication name keys × derived subtype.
// Mapping reflects standard IVF protocol classifications (UI-SPEC §"Curated dropdown values").
const MEDICATION_CATALOG: ReadonlyArray<{
	nameKey: string;
	subtypeKey: string;
}> = [
	{ nameKey: "cetrotide", subtypeKey: "antagonist" },
	{ nameKey: "menopur", subtypeKey: "stimulationInjection" },
	{ nameKey: "gonalF", subtypeKey: "stimulationInjection" },
	{ nameKey: "follistim", subtypeKey: "stimulationInjection" },
	{ nameKey: "ovidrel", subtypeKey: "triggerShot" },
	{ nameKey: "lupron", subtypeKey: "suppressorLupron" },
	{ nameKey: "pregnyl", subtypeKey: "triggerShot" },
	{ nameKey: "crinone", subtypeKey: "progesteroneSupport" },
	{ nameKey: "endometrin", subtypeKey: "progesteroneSupport" },
	{ nameKey: "estrace", subtypeKey: "estrogenSupport" },
	{ nameKey: "vivelleDot", subtypeKey: "estrogenSupport" },
	{ nameKey: "progesteroneInOil", subtypeKey: "progesteroneSupport" },
	{ nameKey: "other", subtypeKey: "other" },
];

function formatDateTime(date: Date, time: string): string {
	const yyyy = date.getFullYear();
	const mm = String(date.getMonth() + 1).padStart(2, "0");
	const dd = String(date.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}T${time}:00`;
}

function getDeviceTimezone(): string {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		return "America/New_York";
	}
}

/**
 * Phase 37 — Parse a stored RRULE string back into a FrequencyValue
 * for edit-mode hydration. Covers the 5 fixed presets + WEEKLY catch-all
 * (RESEARCH §"Edit mode adjustments → Frequency picker").
 */
function parseRRuleToFrequency(
	rrule: string | null | undefined,
): FrequencyValue {
	if (!rrule) {
		return { preset: "once", custom: undefined, rrule: null, firstHour: null };
	}
	if (rrule === "RRULE:FREQ=DAILY") {
		return { preset: "daily", custom: undefined, rrule, firstHour: null };
	}
	if (rrule === "RRULE:FREQ=DAILY;BYHOUR=8,20") {
		return { preset: "bid", custom: undefined, rrule, firstHour: null };
	}
	if (rrule === "RRULE:FREQ=DAILY;BYHOUR=8,14,20") {
		return { preset: "tid", custom: undefined, rrule, firstHour: null };
	}
	if (rrule === "RRULE:FREQ=DAILY;INTERVAL=2") {
		return { preset: "eod", custom: undefined, rrule, firstHour: null };
	}
	if (rrule.startsWith("RRULE:FREQ=WEEKLY")) {
		const byday = /BYDAY=([A-Z,]+)/.exec(rrule);
		const weekdays = byday
			? byday[1]
					.split(",")
					.filter((w): w is Weekday => VALID_WEEKDAYS.has(w as Weekday))
			: [];
		const until = /UNTIL=(\d{4})(\d{2})(\d{2})/.exec(rrule);
		const count = /COUNT=(\d+)/.exec(rrule);
		const custom: CustomFrequencyState = until
			? {
					weekdays,
					endMode: "until",
					until: `${until[1]}-${until[2]}-${until[3]}`,
				}
			: count
				? { weekdays, endMode: "count", count: Number(count[1]) }
				: { weekdays, endMode: "count", count: 1 };
		return { preset: "custom", custom, rrule, firstHour: null };
	}
	return { preset: "once", custom: undefined, rrule: null, firstHour: null };
}

/**
 * Phase 37 — Medication form (ADD-04 + ADD-08 + EDM-04).
 *
 * Field order (per Figma): Medication Name + Type → Date → Time → Dosage →
 *   Frequency → Prescribed By → Notes.
 *
 * Required: medication name, date, time. Subtype is auto-derived from the
 * "Medication Name + Type" dropdown selection (each named medication maps to
 * a known protocol type — e.g. Cetrotide → Antagonist, Menopur → Stimulation).
 *
 * BID/TID hour preservation (WR-05): the user's chosen time is preserved
 * for all presets — server expansion starts from that hour. Earlier drafts
 * silently rewrote the hour to 8 to match the synthetic id ISO; that
 * surprised users who saw e.g. 14:30 in the picker but found the saved
 * event at 08:30. The hour is now whatever the TimePicker reports.
 */
export function MedicationForm({
	mode,
	initialValues,
	onClose,
}: MedicationFormProps) {
	const { t } = useClientTranslations("track");
	const { logTrack } = useAnalytics();
	const { data: providersData } = useUserProviders();
	const providers = providersData?.providers ?? [];

	// Curated key vs free-text "other" fallback for medication name.
	const initialKey = useMemo<string | null>(() => {
		if (!initialValues?.medicationName) return null;
		const match = MEDICATION_CATALOG.find(
			({ nameKey }) =>
				nameKey !== "other" &&
				t(`event.add.subtypes.medicationName.${nameKey}`) ===
					initialValues.medicationName,
		);
		return match?.nameKey ?? "other";
	}, [initialValues, t]);

	const [medicationKey, setMedicationKey] = useState<string | null>(initialKey);
	const [otherMedicationName, setOtherMedicationName] = useState<string>(
		initialKey === "other" ? (initialValues?.medicationName ?? "") : "",
	);

	const [state, setState] = useState<MedicationFormState>(() => {
		if (initialValues) {
			const dt = new Date(initialValues.dateTime);
			const hh = String(dt.getHours()).padStart(2, "0");
			const mm = String(dt.getMinutes()).padStart(2, "0");
			const parsedFreq = parseRRuleToFrequency(initialValues.frequencyRRULE);
			return {
				medicationName: initialValues.medicationName ?? "",
				subtype: initialValues.subtype ?? null,
				date: dt,
				time: `${hh}:${mm}`,
				dosage: initialValues.dosage ?? "",
				frequencyPreset: parsedFreq.preset,
				customFrequency: parsedFreq.custom,
				contactId: initialValues.contactId ?? null,
				notes: initialValues.notes ?? "",
			};
		}
		return {
			medicationName: "",
			subtype: null,
			date: null,
			time: null,
			dosage: "",
			frequencyPreset: "once",
			customFrequency: undefined,
			contactId: null,
			notes: "",
		};
	});

	const [frequency, setFrequency] = useState<FrequencyValue>(() =>
		parseRRuleToFrequency(initialValues?.frequencyRRULE),
	);

	// Effective medication name + subtype derived from selected key.
	const selectedMed = MEDICATION_CATALOG.find(
		(m) => m.nameKey === medicationKey,
	);
	const effectiveMedicationName =
		medicationKey === "other"
			? otherMedicationName.trim()
			: medicationKey
				? t(`event.add.subtypes.medicationName.${medicationKey}`)
				: "";
	const effectiveSubtype = selectedMed?.subtypeKey ?? null;

	// Sync derived medicationName + subtype + frequency into state.
	// Compare custom-frequency by serialised content (NOT referential equality)
	// because FrequencyPicker emits a fresh object literal on every change —
	// `===` always fails and pre-WR-04 relied on React's setState dedup as a
	// safety net (WR-04: brittle, would silently regress on future refactors).
	useEffect(() => {
		setState((s) => {
			const sameCustom =
				JSON.stringify(s.customFrequency) === JSON.stringify(frequency.custom);
			if (
				s.medicationName === effectiveMedicationName &&
				s.subtype === effectiveSubtype &&
				s.frequencyPreset === frequency.preset &&
				sameCustom
			) {
				return s;
			}
			return {
				...s,
				medicationName: effectiveMedicationName,
				subtype: effectiveSubtype,
				frequencyPreset: frequency.preset,
				customFrequency: frequency.custom,
			};
		});
	}, [effectiveMedicationName, effectiveSubtype, frequency]);

	const idempotencyKeyRef = useRef<string | null>(null);
	const addMutation = useAddEvent();
	const updateMutation = useUpdateEvent();
	const isPending = addMutation.isPending || updateMutation.isPending;
	const validation = validateMedicationForm(state);

	const isRecurringInstance =
		mode === "edit" && initialValues
			? parseSyntheticEventId(initialValues.id) !== null
			: false;

	const handleFrequencyChange = (next: FrequencyValue) => {
		setFrequency(next);
		// WR-05: no hour rewrite on frequency change — preserve the user's time.
	};

	const handleSave = () => {
		if (!validation.isValid) return;
		if (!state.date || !state.time) return;
		// WR-05: preserve user-selected hour for BID/TID — server expansion
		// starts from this hour, so the synthetic id ISO will match.
		const dateTime = formatDateTime(state.date, state.time);
		const provider =
			providers.find((p) => p.providerUserId === state.contactId) ?? null;
		// Preserve previously stored doctorName on unchanged-edit when the
		// providers query has not resolved yet — otherwise saving would silently
		// erase prescriber metadata (PR-102 review #4).
		const contactUnchanged =
			mode === "edit" && (initialValues?.contactId ?? null) === state.contactId;
		const doctorName =
			provider?.providerName ??
			(contactUnchanged ? (initialValues?.doctorName ?? null) : null);

		const body: CreateEventInput = {
			type: "medication",
			subtype: state.subtype,
			title: effectiveMedicationName,
			medicationName: effectiveMedicationName,
			dosage: state.dosage.trim() || null,
			doctorName,
			dateTime,
			timezone: initialValues?.timezone ?? getDeviceTimezone(),
			frequencyRRULE: frequency.rrule,
			contactId: state.contactId,
			notes: state.notes.trim() || null,
		};

		if (mode === "create") {
			if (!idempotencyKeyRef.current) {
				idempotencyKeyRef.current = crypto.randomUUID();
			}
			addMutation.mutate(
				{ body, idempotencyKey: idempotencyKeyRef.current },
				{
					onSuccess: () => {
						idempotencyKeyRef.current = null;
						logTrack(TRACK_EVENT_CREATED, {
							eventType: "medication",
							isRecurring: state.frequencyPreset !== "once",
						});
						toast.success(t("event.add.successToast"));
						onClose();
					},
					onError: () => {
						idempotencyKeyRef.current = null;
						toast.error(t("event.add.errors.save"));
					},
				},
			);
			return;
		}

		if (initialValues) {
			const baseId =
				parseSyntheticEventId(initialValues.id)?.baseId ?? initialValues.id;
			updateMutation.mutate(
				{ id: baseId, patch: body },
				{
					onSuccess: () => {
						logTrack(TRACK_EVENT_EDITED, {
							eventType: "medication",
							isRecurring: state.frequencyPreset !== "once",
						});
						toast.success(t("event.edit.successToast"));
						onClose();
					},
					onError: () => {
						toast.error(t("event.edit.errorToast"));
					},
				},
			);
		}
	};

	const medNameId = "medication-name";
	const otherNameId = "medication-other-name";
	const dateId = "medication-date";
	const timeId = "medication-time";
	const dosageId = "medication-dosage";
	const frequencyId = "medication-frequency";
	const contactFieldId = "medication-contact";
	const notesId = "medication-notes";

	const medicationOptions: SelectOption[] = MEDICATION_CATALOG.map(
		({ nameKey, subtypeKey }) => ({
			value: nameKey,
			label: t(`event.add.subtypes.medicationName.${nameKey}`),
			hint:
				nameKey === "other"
					? undefined
					: t(`event.add.subtypes.medicationSubtype.${subtypeKey}`),
		}),
	);

	// Lead with a "None" sentinel so users can clear a previously chosen contact
	// (the field is optional but the dropdown otherwise has no path back to null
	// once a provider is selected — PR-102 review #3).
	const providerOptions: SelectOption[] = [
		{ value: "", label: t("event.add.fields.prescribedBy.none") },
		...providers.map((p) => ({
			value: p.providerUserId,
			label: p.providerName,
		})),
	];

	return (
		<EventFormShell
			mode={mode}
			isValid={validation.isValid}
			isPending={isPending}
			onSave={handleSave}
			recurringBanner={
				isRecurringInstance && initialValues ? (
					<RecurringSeriesBanner type={initialValues.type} variant="edit" />
				) : undefined
			}
		>
			<FormField
				id={medNameId}
				label={t("event.add.fields.medicationName.label")}
				required
			>
				<Select
					id={medNameId}
					value={medicationKey}
					options={medicationOptions}
					placeholder={t("event.add.fields.medicationName.placeholder")}
					onChange={(v) => setMedicationKey(v)}
				/>
			</FormField>

			{medicationKey === "other" ? (
				<FormField
					id={otherNameId}
					label={t("event.add.fields.medicationName.otherLabel")}
					required
				>
					<input
						id={otherNameId}
						type="text"
						className={styles.input}
						value={otherMedicationName}
						placeholder={t("event.add.fields.medicationName.otherPlaceholder")}
						onChange={(e) => setOtherMedicationName(e.target.value)}
					/>
				</FormField>
			) : null}

			<DatePicker
				id={dateId}
				label={t("event.add.fields.date.label")}
				required
				selected={state.date ?? undefined}
				onSelect={(d) => setState((s) => ({ ...s, date: d ?? null }))}
			/>

			<TimePicker
				id={timeId}
				label={t("event.add.fields.time.label")}
				required
				value={state.time ?? undefined}
				onChange={(v) => setState((s) => ({ ...s, time: v }))}
			/>

			<FormField id={dosageId} label={t("event.add.fields.dosage.label")}>
				<input
					id={dosageId}
					type="text"
					className={styles.input}
					value={state.dosage}
					placeholder={t("event.add.fields.dosage.placeholder")}
					onChange={(e) => setState((s) => ({ ...s, dosage: e.target.value }))}
				/>
			</FormField>

			<FrequencyPicker
				id={frequencyId}
				label={t("event.add.fields.frequency.label")}
				value={frequency}
				onChange={handleFrequencyChange}
				accentCategory="meds"
			/>

			<FormField
				id={contactFieldId}
				label={t("event.add.fields.prescribedBy.label")}
			>
				<Select
					id={contactFieldId}
					value={state.contactId}
					options={providerOptions}
					placeholder={t("event.add.fields.prescribedBy.placeholder")}
					onChange={(v) => setState((s) => ({ ...s, contactId: v || null }))}
					leading={
						<UserIcon className={styles.leadingIcon} aria-hidden="true" />
					}
				/>
			</FormField>

			<FormField id={notesId} label={t("event.add.fields.notes.label")}>
				<textarea
					id={notesId}
					className={styles.textarea}
					value={state.notes}
					placeholder={t("event.add.fields.notes.placeholder")}
					onChange={(e) => setState((s) => ({ ...s, notes: e.target.value }))}
				/>
			</FormField>
		</EventFormShell>
	);
}
